/**
 * How a paged games list stays whole across refetches (useGames). The list arrives as a 25-game first page, then
 * 500-game pages in the background. A refetch used to REPLACE the list with its first page and never page the
 * rest again (the guard was set once), so after 10 minutes away the Library or Practice showed 25 games (perf
 * survey #4, 2026-10-13). Now the fresh first page lands on top of what's shown, and each later page refreshes
 * its games in place.
 */

interface Identified {
  readonly gameOriginId: string;
}

/** A refetch's first page over the list already shown: the page first (new games on top, its versions win),
 *  then every shown game it doesn't contain, in order. The later pages refresh those in place. */
export function seedFromPrevious<T extends Identified>(prev: readonly T[] | undefined, page: readonly T[]): T[] {
  if (!prev?.length) return [...page];
  const onPage = new Set(page.map((g) => g.gameOriginId));
  return [...page, ...prev.filter((g) => !onPage.has(g.gameOriginId))];
}

/** How long the paging waits before asking a failed page again: twice, after a second and then three. A page that
 *  failed once used to end the paging, and the Library's counts wait for every page (#351). */
export const PAGE_RETRY_MS: readonly number[] = [1000, 3000];

const sleep = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });

/** `read`, asked again after each of `delays` while it throws; the last error when every try fails. */
export async function retried<T>(read: () => Promise<T>, delays: readonly number[], wait: (ms: number) => Promise<void> = sleep): Promise<T> {
  for (let i = 0; ; i++) {
    try {
      return await read();
    } catch (e) {
      if (i >= delays.length) throw e;
      await wait(delays[i]);
    }
  }
}

/** A later page into the list: a game already listed is replaced where it is, an unseen one is appended. */
export function mergePage<T extends Identified>(current: readonly T[], page: readonly T[]): T[] {
  const fresh = new Map(page.map((g) => [g.gameOriginId, g]));
  const merged = current.map((g) => fresh.get(g.gameOriginId) ?? g);
  const listed = new Set(current.map((g) => g.gameOriginId));
  return [...merged, ...page.filter((g) => !listed.has(g.gameOriginId))];
}
